import { useApi } from '@backstage/core-plugin-api';
import type { ControlPlaneSkillPlanProjection } from '@internal/spassu-code-contract';
import { useCallback, useEffect, useState } from 'react';
import { controlPlaneApiRef } from '../api';

export interface ControlPlaneSkillPlanView {
  plan: ControlPlaneSkillPlanProjection | null;
  loading: boolean;
  error: string | null;
  retry(): void;
}

export function useControlPlaneSkillPlan(
  activityId: string,
): ControlPlaneSkillPlanView {
  const api = useApi(controlPlaneApiRef);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<ControlPlaneSkillPlanView, 'retry'>>({
    plan: null,
    loading: true,
    error: null,
  });
  const retry = useCallback(() => setRevision(value => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setState(current => ({ ...current, loading: true, error: null }));
    void api
      .getSkillPlan(activityId, controller.signal)
      .then(plan => {
        if (!cancelled) setState({ plan, loading: false, error: null });
      })
      .catch(caught => {
        if (cancelled || controller.signal.aborted) return;
        const message =
          caught instanceof Error
            ? caught.message
            : 'Não foi possível carregar as skills da atividade.';
        setState({ plan: null, loading: false, error: message });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activityId, api, revision]);

  return { ...state, retry };
}
